import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  MouseEvent as ReactMouseEvent,
  CSSProperties,
} from "react"

import { FocusManagementContext, FocusManager } from "./FocusManager"
import { GetCellPropsFn, GetRowPropsFn, TableProps } from "./types"
import {
  HEADER_HEIGHT,
  OVERSCAN_COUNT,
  ROW_HEIGHT,
  DEFAULT_PAGE_SIZE,
  DEFAULT_OFFSET,
} from "./constants"

import { ColumnManager } from "./ColumnManager"
import { Grid } from "./Grid"
import { Loading } from "components/Loading"
import { TableContext } from "./context"
import { TableDataProvider } from "./data/context"
import { cx } from "@emotion/css"
import { styles } from "./styles"
import { useCurrent } from "./useCurrent"
import { useScroll } from "react-use-gesture"

import { TablePagination, IconButton } from "@material-ui/core"
import FirstPageIcon from "@material-ui/icons/FirstPage"
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft"
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight"
import LastPageIcon from "@material-ui/icons/LastPage"

import usePagination from "hooks/usePagination"
import { SpotsTablePaginationActionsProps } from "models/spots"
import { PageInput } from "components/inputs/PageInput"

const empty_object = {}
const _defaultGetCellProps: GetCellPropsFn = () => empty_object
const _defaultGetRowProps: GetRowPropsFn = () => empty_object

export function Table<T extends { id: string }>(props: TableProps<T>) {
  const {
    source,
    columns,
    //
    rowHeight = ROW_HEIGHT,
    headerHeight = HEADER_HEIGHT,
    overscanCount = OVERSCAN_COUNT,
    //
    width = "100%",
    height,
    className,
    onColumnResize: onColumnResizeProp,
    getRowProps = _defaultGetRowProps,
    getCellProps = _defaultGetCellProps,
    emptyClassName,
    emptyText,
    pending = false,
    rowHoverActions,
    //
    onSort,
    sortingByColumn,
    //
    pagination = false,
    onChangePagination,
    paginationData,
  } = props

  const { itemCount, getItemKey, sort } = source

  const [, getInstance] = useCurrent(() => ({
    getRowProps,
    getCellProps,
    onColumnResize: onColumnResizeProp,
    rowHoverActions,
  }))

  Object.assign(getInstance(), {
    getRowProps,
    getCellProps,
    onColumnResize: onColumnResizeProp,
    rowHoverActions,
  })

  const [version, setVersion] = useState<number>(() => 0)
  const onUpdate = () => setVersion((v) => v + 1)

  // if we have fixed columns, we need to sync the hover state across the inner grids
  const [hoveredRow, setHoveredRow] = useState<number | null>(() => null)
  const onRowHover = useCallback((index: number, hovered: boolean) => {
    setHoveredRow((_) => (hovered ? index : null))
  }, [])

  //#region sorting
  //columnId
  const [activeSortColumn, setActiveSortColumn] = useState<string>("")
  // false: descending, true: ascending
  const [selectedSortOrder, setSelectedSortOrder] = useState<boolean>(false)

  const onSortColumnChange = useCallback((id: string) => {
    setActiveSortColumn(id)
    setSelectedSortOrder((prev) => !prev)
  }, [])

  useEffect(() => {
    if (!onSort) return
    if (!activeSortColumn) return
    const sorter = (a: T, b: T) => {
      const res = onSort(activeSortColumn, a, b)
      return selectedSortOrder ? res : -res
    }
    sort(sorter)
  }, [activeSortColumn, selectedSortOrder, sort, onSort])
  //#endregion

  //#region Column Management
  const [cm, getColumnManager] = useCurrent(
    () => new ColumnManager({ columns, onUpdate: onUpdate }),
  )
  cm.onReceiveColumns(columns)
  //#endregion

  //#region Focus Management
  const columnsCount = columns.length
  const [fm, getFm] = useCurrent(
    () => new FocusManager(itemCount, columnsCount),
  )
  useEffect(() => {
    getFm().setDimensions(itemCount, columnsCount)
  }, [getFm, itemCount, columnsCount])
  //#endregion

  const containerRef = useRef<HTMLDivElement | null>(null)

  //#region Virtualize
  const scroller = useRef<HTMLDivElement | null>(null)
  const observer = useCallback(() => ({ height, width: 0 }), [height])
  const estimateSize = useCallback(() => rowHeight, [rowHeight])
  const virtualOptions = useMemo(() => {
    return {
      parentRef: scroller,
      size: itemCount,
      overscan: overscanCount,
      keyExtractor: getItemKey,
      estimateSize: estimateSize,
      useObserver: observer,
    }
  }, [scroller, itemCount, overscanCount, observer, estimateSize, getItemKey])
  //#endregion

  const [scrollingFast, setFast] = useState<boolean>(() => false)
  useScroll(
    ({ scrolling, velocity, direction: [, vertical] }) => {
      const pointers = scrolling ? "none" : "auto"
      containerRef.current?.style.setProperty("--pointer-events", pointers)
      const a = vertical ? 0 : 1
      containerRef.current?.style.setProperty("--a", a.toString())
      const isFast = velocity > 6
      setFast((prev) => (prev === isFast ? prev : isFast))
    },
    {
      domTarget: scroller,
    },
  )

  // Pagination
  const { page, changePage, pageSize, changePageSize } = usePagination()

  const currentPageInfo = paginationData ?? {
    pageSize: DEFAULT_PAGE_SIZE,
    offset: DEFAULT_OFFSET,
    total: DEFAULT_PAGE_SIZE,
  }

  const calculatePage = () => currentPageInfo.offset / currentPageInfo.pageSize

  useEffect(() => {
    changePageSize(currentPageInfo.pageSize)
    changePage(calculatePage())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationData])

  const handleChangePagination = (newPage: number, newPageSize: number) => {
    if (newPageSize !== pageSize) {
      changePageSize(newPageSize)
      newPage = 0
    }
    changePage(newPage)

    if (onChangePagination)
      onChangePagination({
        pageSize: newPageSize,
        offset: newPageSize * newPage,
      })
  }

  function TablePaginationActions(props: SpotsTablePaginationActionsProps) {
    const { onChangePage } = props
    const actualValue = String(page + 1)

    const handleFirstPageButtonClick = (
      event: ReactMouseEvent<HTMLButtonElement>,
    ) => {
      onChangePage(event, 0)
    }

    const handleBackButtonClick = (
      event: ReactMouseEvent<HTMLButtonElement>,
    ) => {
      onChangePage(event, calculatePage() - 1)
    }

    const handleNextButtonClick = (
      event: ReactMouseEvent<HTMLButtonElement>,
    ) => {
      onChangePage(event, calculatePage() + 1)
    }

    const handleLastPageButtonClick = (
      event: ReactMouseEvent<HTMLButtonElement>,
    ) => {
      onChangePage(event, Math.ceil(currentPageInfo.total / pageSize) - 1)
    }

    const totalPages = Math.ceil(currentPageInfo.total / pageSize)

    return (
      <div className={styles.paginationContainer}>
        <IconButton
          onClick={handleFirstPageButtonClick}
          disabled={calculatePage() === 0}
          aria-label="first page"
        >
          <FirstPageIcon />
        </IconButton>

        <IconButton
          onClick={handleBackButtonClick}
          disabled={calculatePage() === 0}
          aria-label="previous page"
        >
          <KeyboardArrowLeft />
        </IconButton>

        <span>
          Page{" "}
          <PageInput
            actualValue={actualValue}
            totalPages={totalPages}
            pageSize={pageSize}
            handleChangePagination={handleChangePagination}
          />{" "}
          of {totalPages}
        </span>
        <IconButton
          onClick={handleNextButtonClick}
          disabled={calculatePage() >= totalPages - 1}
          aria-label="next page"
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton
          onClick={handleLastPageButtonClick}
          disabled={calculatePage() >= totalPages - 1}
          aria-label="last page"
        >
          <LastPageIcon />
        </IconButton>
      </div>
    )
  }

  //#region context
  const ctx = useMemo(() => {
    const columnManager = getColumnManager()
    return {
      columnManager,
      ...getInstance(),
      onRowHover: columnManager.hasFixedColumns ? onRowHover : undefined,
      hoveredRow,
      activeSortColumn,
      selectedSortOrder,
      onSortColumnChange,
      scrollingFast,
    }
    //eslint-disable-next-line
  }, [
    getInstance,
    getColumnManager,
    version,
    onRowHover,
    hoveredRow,
    activeSortColumn,
    selectedSortOrder,
    onSortColumnChange,
    scrollingFast,
  ])
  //#endregion

  const vars = {
    "--viewport-height": pagination
      ? `${ROW_HEIGHT * (DEFAULT_PAGE_SIZE + 1) + 12}px`
      : `${height}px`,
    "--header-height": `${headerHeight}px`,
    "--row-height": `${rowHeight}px`,
    ...cm.layoutVariables,
  } as CSSProperties

  //#region overlays
  const empty = columnsCount === 0 || itemCount === 0
  const emptyOverlay = <div className={emptyClassName}>{emptyText}</div>
  //#endregion

  return pending ? (
    <Loading />
  ) : (
    <TableContext.Provider value={ctx}>
      <TableDataProvider source={source}>
        <FocusManagementContext.Provider value={fm}>
          {empty ? (
            emptyOverlay
          ) : (
            <>
              <div
                ref={containerRef}
                className={cx(styles.container, className)}
                style={{ ...vars, width }}
              >
                <div ref={scroller} className={styles.scroller}>
                  {cm.hasFixedColumns ? (
                    <Grid
                      area="fixed"
                      virtualOptions={virtualOptions}
                      sortingByColumn={sortingByColumn}
                      pagination={pagination}
                    />
                  ) : null}
                  <Grid
                    area="main"
                    virtualOptions={virtualOptions}
                    sortingByColumn={sortingByColumn}
                    pagination={pagination}
                  />
                </div>
              </div>
              {pagination && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={currentPageInfo.total}
                  rowsPerPage={pageSize}
                  page={page}
                  onChangePage={(_: any, newPage: number) =>
                    handleChangePagination(newPage, pageSize)
                  }
                  onChangeRowsPerPage={(evt) =>
                    handleChangePagination(page, parseInt(evt.target.value))
                  }
                  classes={{ caption: "BodyBody-sDark-greyRegular" }}
                  ActionsComponent={TablePaginationActions}
                />
              )}
            </>
          )}
        </FocusManagementContext.Provider>
      </TableDataProvider>
    </TableContext.Provider>
  )
}
